import { Footer } from "@/components/footer"
import { Hotel, Palmtree, UtensilsCrossed, PartyPopper, Sparkles, Car } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SEO } from "@/components/SEO"

export default function CategoriesPage() {
  const categories = [
    {
      icon: Hotel,
      title: "Accommodation",
      description: "Hotels, resorts, vacation rentals, and unique stays",
      features: ["Verified properties", "Flexible pricing", "Instant booking", "24/7 support"],
    },
    {
      icon: Palmtree,
      title: "Safari & Adventures",
      description: "Wildlife tours, nature expeditions, and outdoor experiences",
      features: ["Expert guides", "Custom itineraries", "Group & private tours", "All equipment included"],
    },
    {
      icon: UtensilsCrossed,
      title: "Restaurants & Dining",
      description: "Fine dining, casual eats, and culinary experiences",
      features: ["Table reservations", "Special menus", "Dietary options", "Event catering"],
    },
    {
      icon: PartyPopper,
      title: "Nightlife & Entertainment",
      description: "Nightclubs, bars, live music, and entertainment venues",
      features: ["VIP table booking", "Event access", "Bottle service", "Guest list entry"],
    },
    {
      icon: Sparkles,
      title: "Beauty & Wellness",
      description: "Spas, salons, fitness centers, and wellness services",
      features: ["Spa packages", "Beauty treatments", "Fitness classes", "Wellness programs"],
    },
    {
      icon: Car,
      title: "Car Hire & Transport",
      description: "Vehicle rentals, chauffeur services, and airport transfers",
      features: ["Wide vehicle selection", "Flexible rental terms", "Insurance included", "Delivery available"],
    },
  ]

  return (
    <>
      <SEO
        title="Service Categories - StayApp | Accommodation, Safari, Dining, Beauty & More"
        description="Explore all service categories on StayApp - book accommodation, safari trips, restaurants, nightclubs, beauty & wellness services, and car hire with negotiable pricing."
        keywords="service categories, accommodation booking, safari trips, restaurant reservations, nightlife booking, beauty services, car hire"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/categories"
        canonical="https://stayapp.com/categories"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-[#75055B] px-2">
            Service Categories
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Discover and book services across multiple categories - all with negotiable pricing and verified providers
          </p>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {categories.map((category, index) => (
              <div
                key={index}
                className="group p-6 sm:p-8 rounded-2xl border border-gray-200 hover:border-[#DB4789] hover:shadow-xl hover:shadow-[#DB4789]/10 transition-all duration-300"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#75055B]/10 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <category.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#75055B]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-2 sm:mb-3">{category.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{category.description}</p>
                <ul className="space-y-2 mb-4 sm:mb-6">
                  {category.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-xs sm:text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#75055B] mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  variant="outline"
                  className="w-full border-[#75055B] text-[#75055B] hover:bg-[#DB4789] hover:text-white hover:border-[#DB4789] bg-transparent text-sm sm:text-base"
                >
                  Explore {category.title}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-[#75055B]">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">Ready to Book?</h2>
          <p className="text-base sm:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Download StayApp now and start negotiating the best deals across all categories
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Button
              size="lg"
              className="bg-white text-black hover:bg-gray-100 shadow-lg px-6 sm:px-8 w-full sm:w-auto"
            >
              Download for iOS
            </Button>
            <Button
              size="lg"
              className="bg-[#DB4789] text-white hover:bg-[#DB4789]/90 shadow-lg px-6 sm:px-8 w-full sm:w-auto"
            >
              Download for Android
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

